// DATA CURRENCY — "does this snapshot already reflect the writes the server has applied?"
//
// THE AXIS THIS IS NOT
//   Request ownership (`utils/latestOnly.js`) answers "is my response the newest of its kind?".
//   A successful DELETE used to answer the currency question by claiming the READ token, i.e. by
//   saying "every read in flight is now invalid". That is strictly wider than the truth and it
//   shipped as a bug: a read the operator started for a DIFFERENT date range carries nothing the
//   delete makes stale, and discarding it left the table empty under a filter that had rows —
//   while the action still reported the list as current.
//
// WHAT IS ACTUALLY TRUE
//   Only a snapshot the server produced BEFORE the delete can still contain the deleted row. So
//   the read records the version it started at, the mutation records what it changed, and the read
//   replays exactly the changes that happened after it started. A read that started after the write
//   is passed through untouched, and rows the write says nothing about are passed through
//   untouched. That is a reconcile-forward, not an invalidation, which is why the read can still
//   honestly report that it established a current list.
//
//   TWO KINDS OF CHANGE, one ledger. `removed(key)` drops a row; `updated(key, patch)` replays a
//   field write onto it. They are the same question — "is this snapshot older than something the
//   server has already done?" — and a queue-setting toggle needed the second one for exactly the
//   reason a booking delete needed the first: the server had accepted the change, and a snapshot
//   taken before it was putting the old value back for good.
//
// IDENTITY IS COMPARED AS A STRING, AND AN UNIDENTIFIABLE ROW IS KEPT
//   The two sides of this join come from two different responses, so `123` and `'123'` must match
//   or a deleted row silently comes back — the exact failure this module exists to prevent. And a
//   row whose key is missing (or a row that is not an object at all) is KEPT: removing it would
//   mean one unidentifiable row is matched by any other, so a single delete could empty a table.
//   For the same reason `removed()` refuses a null/undefined key instead of recording one.
//
// LIFETIME
//   An entry matters only while a read that started BEFORE it is still in flight. So the ledger is
//   pruned whenever that set changes — a removal recorded with no read in flight is never stored at
//   all, and entries older than the oldest live read are dropped when a read settles. Size is
//   therefore bounded by the deletes that actually overlap a live read, not by the life of the tab.
//   THE EXCEPTION, stated as what it costs rather than as a footnote: a read that never settles
//   holds its slot open, and while it does, EVERY subsequent write is retained — 10,000 writes
//   measured as 10,000 entries, cleared the instant that read settles. The app sets no request
//   timeout (`src/apis/axios.js`), so the bound is "writes that overlap the longest live read", not
//   "writes per session". That hung read is already holding a socket, so this adds no NEW failure
//   mode — but "does not grow without bound" would be too strong a sentence, and is not made here.
export function createDataVersion() {
  let version = 0
  let applied = []
  // The `version` each in-flight read started at. A multiset: two reads can start at the same one.
  let live = []
  // WHICH WRITE OWNS EACH FIELD. Ledger order is RESPONSE-ARRIVAL order, not server-apply order, so
  // "the later entry wins" is wrong whenever two writes to the same field answer out of order: the
  // loser's value would be replayed over the winner's, and — because reconcile now overwrites the
  // server's own snapshot — it would stick, which is WORSE than not recording at all. A write
  // therefore announces itself when it STARTS and may only record the fields no later write has
  // claimed since.
  let write_seq = 0
  const owner = new Map()

  const identity = key => (key === null || key === undefined ? null : String(key))

  // PRIVATE. It was public and `this`-bound, which meant `record(key)` with one argument was
  // indistinguishable from a removal and silently dropped the row, and destructuring the module
  // broke `removed`/`updated` outright.
  const record = (id, patch) => {
    if (id === null || patch === undefined) return
    version += 1
    // With no read in flight there is no snapshot that can predate this, so it is spent on arrival
    // — and saying so directly keeps recording O(1) instead of filtering the whole ledger each time.
    if (!live.length) {
      applied = []
      return
    }
    applied.push({ key: id, version, patch })
  }

  const prune = () => {
    if (!live.length) {
      applied = []
      return
    }
    const oldest = Math.min(...live)
    applied = applied.filter(entry => entry.version > oldest)
  }

  return {
    // Call when a READ starts.
    beginRead() {
      const seen = version
      live.push(seen)
      let settled = false
      return {
        // `rows` as the server sent them; `keyOf` maps a row to the identity mutations record.
        // Returns the rows that are still real.
        reconcile(rows, keyOf) {
          // Loud, because the alternative is silent: after settle() this read no longer holds the
          // entries it would need, so it would resurrect every removed row and look fine doing it.
          if (settled) throw Error('dataVersion: reconcile() called after settle()')
          // A response shape this module cannot index is passed through rather than emptied —
          // the caller's own success check owns that case.
          if (!Array.isArray(rows)) return rows
          const pending = applied.filter(entry => entry.version > seen)
          if (!pending.length) return rows
          const dropped = new Set(pending.filter(entry => !entry.patch).map(entry => entry.key))
          // Later patches win over earlier ones for the same row; a removal is terminal.
          const patches = new Map()
          for (const entry of pending) {
            if (!entry.patch) continue
            patches.set(entry.key, { ...(patches.get(entry.key) || {}), ...entry.patch })
          }
          const out = []
          for (const row of rows) {
            const key = row === null || typeof row !== 'object' ? null : identity(keyOf(row))
            if (key !== null && dropped.has(key)) continue
            const patch = key === null ? null : patches.get(key)
            out.push(patch ? { ...row, ...patch } : row)
          }
          return out
        },
        // Call on EVERY exit of the read — success, failure, superseded, throw.
        settle() {
          if (settled) return
          settled = true
          live.splice(live.indexOf(seen), 1)
          prune()
        }
      }
    },
    // Call when a WRITE STARTS. `accepted()` records only the fields still owned by this write,
    // and returns them, so the caller reconciles exactly what it was allowed to record.
    beginWrite(key, patch) {
      const id = identity(key)
      const fields = patch && typeof patch === 'object' ? Object.keys(patch) : []
      const seq = ++write_seq
      for (const field of fields) owner.set(`${id}\u0000${field}`, seq)
      return {
        accepted() {
          const mine = {}
          for (const field of fields) {
            if (owner.get(`${id}\u0000${field}`) !== seq) continue
            mine[field] = patch[field]
            owner.delete(`${id}\u0000${field}`)
          }
          record(id, Object.keys(mine).length ? mine : undefined)
          return mine
        }
      }
    },
    // Call from a MUTATION the server has already applied: the row is gone.
    removed(key) {
      record(identity(key), null)
    },
    // Call from a MUTATION the server has already applied: these fields now hold these values.
    // Prefer beginWrite()/accepted() where two writes to one field can overlap.
    updated(key, patch) {
      if (!patch || typeof patch !== 'object' || !Object.keys(patch).length) return
      record(identity(key), { ...patch })
    },
    // For the tests: the ledger must not grow without bound.
    size() {
      return applied.length
    }
  }
}
